import { useState, useEffect, useRef } from 'react';
import type { AddStudentData } from './AddStudent';
import {
    fetchStudent,
    updateStudent,
    fetchClasses,
    fetchSections,
    fetchStreams,
    fetchStates,
    fetchDistricts,
    fetchCities,
    studentTransportRouteId,
} from '../../services/studentApi';
import type {
    ClassOption,
    SectionOption,
    StreamOption,
    StateOption,
    DistrictOption,
    CityOption,
} from '../../services/studentApi';
import { fetchTransportRoutes, type TransportRouteRecord } from '../../services/transportRouteApi';
import { routeMonthlyFeeLabel } from './routeFee';
import type { ApiError } from '../../services/api';
import { getSchoolId } from '../../services/auth';
import './AddStudent.css';
import './EditStudent.css';
import { useTranslation } from 'react-i18next';

interface EditStudentProps {
    studentId: string;
    onClose: () => void;
    onSave: () => void;
}

/* The edit form covers every field the update API accepts — a few more than
   the create form (student email/phone, enrollment number, roll number,
   admission date), so it gets its own data shape. */
interface EditStudentData extends AddStudentData {
    email: string;
    phone: string;
    enrollment_number: string;
    roll_number: string;
    admission_date: string;
}

const INITIAL_FORM: EditStudentData = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    aadhaar_number: '',
    address: '',
    state: '',
    district: '',
    city: '',
    postal_code: '',
    country: '',
    enrollment_number: '',
    roll_number: '',
    admission_date: '',
    enrollment_status: 'active',
    class_name: '',
    section_name: '',
    father_first_name: '',
    father_last_name: '',
    father_phone: '',
    father_email: '',
    mother_first_name: '',
    mother_last_name: '',
    mother_phone: '',
    mother_email: '',
};

type FieldErrors = Partial<Record<keyof EditStudentData, string>>;

export default function EditStudent({ studentId, onClose, onSave }: EditStudentProps) {
    const { t } = useTranslation();
    const [form, setForm] = useState<EditStudentData>({ ...INITIAL_FORM });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    /* ---- Initial load state ---- */
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    /* ---- Linked father / mother parent records ---- */
    const [fatherId, setFatherId] = useState<string | null>(null);
    const [motherId, setMotherId] = useState<string | null>(null);

    /* ---- The student's school (echoed back on save) ---- */
    const [studentSchoolId, setStudentSchoolId] = useState<string | null>(null);

    /* ---- Profile photo ---- */
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [profilePreview, setProfilePreview] = useState<string | null>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);

    /* ---- API-driven dropdowns ---- */
    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);
    const [availableStreams, setAvailableStreams] = useState<StreamOption[]>([]);
    const [availableStates, setAvailableStates] = useState<StateOption[]>([]);
    const [availableDistricts, setAvailableDistricts] = useState<DistrictOption[]>([]);
    const [availableCities, setAvailableCities] = useState<CityOption[]>([]);
    const [availableRoutes, setAvailableRoutes] = useState<TransportRouteRecord[]>([]);
    const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [selectedStreamId, setSelectedStreamId] = useState('');
    const [selectedRouteId, setSelectedRouteId] = useState('');

    /* The student's state name and section id are resolved against API lists
       after those lists load — held here until they can be applied. */
    const [pendingStateName, setPendingStateName] = useState<string | null>(null);
    const pendingSectionIdRef = useRef<string>('');

    /* ---- Load reference data + the student record ---- */
    useEffect(() => {
        fetchClasses().then(setAvailableClasses).catch(() => { /* silently fall back */ });
        fetchStreams().then(setAvailableStreams).catch(() => { /* silently fall back */ });
        fetchStates().then(setAvailableStates).catch(() => { /* silently fall back */ });
        fetchTransportRoutes().then(setAvailableRoutes).catch(() => { /* silently fall back */ });

        setLoading(true);
        setLoadError(null);
        fetchStudent(studentId)
            .then((s) => {
                const u = s.user;
                const parents = s.parents ?? [];
                // Existing records store the parent's relation as free text — match
                // it to bucket into Father/Mother; fall back to array order.
                const fatherParent = parents.find((p) => (p.relationship_type ?? '').toLowerCase() === 'father')
                    ?? parents[0] ?? null;
                const motherParent = parents.find((p) => (p.relationship_type ?? '').toLowerCase() === 'mother')
                    ?? parents.find((p) => p.id !== fatherParent?.id) ?? null;
                setForm({
                    first_name: u?.first_name ?? s.first_name ?? '',
                    last_name: u?.last_name ?? s.last_name ?? '',
                    email: u?.email ?? '',
                    phone: u?.phone ?? '',
                    date_of_birth: u?.date_of_birth ?? '',
                    gender: (u?.gender as AddStudentData['gender']) ?? '',
                    aadhaar_number: s.aadhaar_number ?? '',
                    address: u?.address ?? '',
                    state: u?.state ?? '',
                    district: '', // not returned by the detail API — re-select if needed
                    city: u?.city ?? '',
                    postal_code: u?.postal_code ?? '',
                    country: u?.country ?? '',
                    enrollment_number: s.enrollment_number ?? '',
                    roll_number: s.roll_number ?? '',
                    // API may return a datetime — the date input needs YYYY-MM-DD
                    admission_date: (s.admission_date ?? '').slice(0, 10),
                    enrollment_status: s.enrollment_status,
                    class_name: s.current_class?.name ?? '',
                    section_name: s.current_section?.name ?? '',
                    father_first_name: fatherParent?.first_name ?? '',
                    father_last_name: fatherParent?.last_name ?? '',
                    father_phone: fatherParent?.phone ?? '',
                    father_email: fatherParent?.email ?? '',
                    mother_first_name: motherParent?.first_name ?? '',
                    mother_last_name: motherParent?.last_name ?? '',
                    mother_phone: motherParent?.phone ?? '',
                    mother_email: motherParent?.email ?? '',
                });
                setFatherId(fatherParent?.id ?? null);
                setMotherId(motherParent?.id ?? null);
                setStudentSchoolId(s.school?.id ?? null);
                setProfilePreview(u?.profile_photo_url ?? u?.profile_photo_thumb_url ?? null);
                setSelectedClassId(s.current_class?.id ?? '');
                setSelectedStreamId(s.stream_id ?? '');
                setSelectedRouteId(studentTransportRouteId(s) ?? '');
                pendingSectionIdRef.current = s.current_section?.id ?? '';
                setPendingStateName(u?.state ?? null);
            })
            .catch((err: ApiError) => setLoadError(err.message || t('editStudent.failedToLoad')))
            .finally(() => setLoading(false));
    }, [studentId]);

    /* Cascade: load sections whenever the selected class changes */
    useEffect(() => {
        if (selectedClassId) {
            fetchSections(selectedClassId).then(setAvailableSections).catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
    }, [selectedClassId]);

    /* Apply the student's pending section id once its class sections have loaded */
    useEffect(() => {
        const pending = pendingSectionIdRef.current;
        if (pending && availableSections.some((s) => s.id === pending)) {
            const sec = availableSections.find((s) => s.id === pending);
            setSelectedSectionId(pending);
            setForm((prev) => ({ ...prev, section_name: sec?.name ?? '' }));
            pendingSectionIdRef.current = '';
        }
    }, [availableSections]);

    /* Resolve the student's state name to a state id once the states list loads */
    useEffect(() => {
        if (pendingStateName && availableStates.length > 0) {
            const st = availableStates.find((s) => s.name === pendingStateName);
            if (st) setSelectedStateId(st.id);
            setPendingStateName(null);
        }
    }, [pendingStateName, availableStates]);

    /* Cascade: load districts and cities whenever the selected state changes */
    useEffect(() => {
        if (selectedStateId !== null) {
            fetchDistricts(selectedStateId).then(setAvailableDistricts).catch(() => setAvailableDistricts([]));
            fetchCities(selectedStateId).then(setAvailableCities).catch(() => setAvailableCities([]));
        } else {
            setAvailableDistricts([]);
            setAvailableCities([]);
        }
    }, [selectedStateId]);

    const selectedRoute = availableRoutes.find((r) => r.id === selectedRouteId);

    /* ---- Handlers ---- */
    const handleChange = (field: keyof EditStudentData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setProfilePhoto(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setProfilePreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        setProfilePreview(null);
        if (photoInputRef.current) photoInputRef.current.value = '';
    };

    /* Whether the mother section has data worth submitting — mother is optional */
    const motherTouched = (): boolean =>
        !!motherId ||
        !!form.mother_first_name.trim() ||
        !!form.mother_last_name.trim() ||
        !!form.mother_phone.trim() ||
        !!form.mother_email.trim();

    const validate = (): boolean => {
        const errs: FieldErrors = {};
        // Same mandatory fields as Add Student: student first name + father
        // details. Student email/phone and mother details are optional.
        if (!form.first_name.trim()) errs.first_name = t('addStudent.firstNameRequired');
        if (!form.father_first_name.trim()) errs.father_first_name = t('addStudent.fatherFirstNameRequired');
        if (!form.father_phone && !form.father_email) {
            errs.father_phone = t('addStudent.phoneOrEmailRequired');
            errs.father_email = t('addStudent.phoneOrEmailRequired');
        }
        if (form.father_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.father_email)) {
            errs.father_email = t('addStudent.invalidEmailFormat');
        }
        if (form.mother_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.mother_email)) {
            errs.mother_email = t('addStudent.invalidEmailFormat');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        setApiError(null);

        try {
            /* The update endpoint expects multipart/form-data. Scalar fields are
               sent even when blank so clearing a field clears it server-side
               (Laravel converts empty strings to null). */
            const payload = new FormData();

            // Personal
            payload.append('first_name', form.first_name);
            payload.append('last_name', form.last_name);
            payload.append('email', form.email);
            payload.append('phone', form.phone);
            payload.append('date_of_birth', form.date_of_birth);
            payload.append('gender', form.gender);
            payload.append('aadhaar_number', form.aadhaar_number);

            // Address
            payload.append('address', form.address);
            payload.append('state', form.state);
            payload.append('district', form.district);
            payload.append('city', form.city);
            payload.append('postal_code', form.postal_code);
            payload.append('country', form.country);

            // School context — echo the student's own school so the update keeps it
            const schoolId = studentSchoolId ?? getSchoolId();
            if (schoolId) payload.append('school_id', schoolId);

            // Academic
            payload.append('enrollment_number', form.enrollment_number);
            payload.append('roll_number', form.roll_number);
            payload.append('admission_date', form.admission_date);
            payload.append('enrollment_status', form.enrollment_status);
            payload.append('current_class_id', selectedClassId);
            payload.append('current_section_id', selectedSectionId);
            payload.append('stream_id', selectedStreamId);

            // Transport — blank clears the student's route
            payload.append('transport_route_id', selectedRouteId);

            // Father — required by validate(), so always sent. father_id targets
            // the existing linked parent record.
            if (fatherId) payload.append('father_id', fatherId);
            payload.append('parent_first_name', form.father_first_name);
            payload.append('father_last_name', form.father_last_name);
            payload.append('parent_phone', form.father_phone);
            payload.append('father_email', form.father_email);

            // Mother — optional; only sent when the section has data
            if (motherTouched()) {
                if (motherId) payload.append('mother_id', motherId);
                payload.append('mother_first_name', form.mother_first_name);
                payload.append('mother_last_name', form.mother_last_name);
                payload.append('mother_phone', form.mother_phone);
                payload.append('mother_email', form.mother_email);
            }

            // Profile photo — only sent when a new file was picked
            if (profilePhoto) payload.append('profile_photo', profilePhoto);

            await updateStudent(studentId, payload);
            onSave();
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrs: FieldErrors = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrs[key as keyof EditStudentData] = msgs[0];
                }
                setErrors(fieldErrs);
            }
            setApiError(apiErr.message || t('editStudent.failedToUpdate'));
        } finally {
            setSaving(false);
        }
    };

    /* ---- Render helpers ---- */
    const fieldClass = (field: keyof EditStudentData) =>
        `add-student__input ${errors[field] ? 'add-student__input--error' : ''}`;

    /* ---- Header (shared across loading / error / loaded states) ---- */
    const header = (
        <header className="add-student__header">
            <h2 className="add-student__title">{t('editStudent.title')}</h2>
            <button className="add-student__close" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </header>
    );

    /* ---- Loading state ---- */
    if (loading) {
        return (
            <>
                <div className="add-student__backdrop" onClick={onClose} />
                <aside className="add-student" role="dialog" aria-label={t('editStudent.title')}>
                    {header}
                    <div className="edit-student__loading">
                        <span className="edit-student__loading-dot" />
                        <span className="edit-student__loading-dot" />
                        <span className="edit-student__loading-dot" />
                    </div>
                </aside>
            </>
        );
    }

    /* ---- Load error state ---- */
    if (loadError) {
        return (
            <>
                <div className="add-student__backdrop" onClick={onClose} />
                <aside className="add-student" role="dialog" aria-label={t('editStudent.title')}>
                    {header}
                    <div className="add-student__error-banner" role="alert">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="12" cy="16" r="1" fill="currentColor" />
                        </svg>
                        <span>{loadError}</span>
                    </div>
                </aside>
            </>
        );
    }

    return (
        <>
            <div className="add-student__backdrop" onClick={onClose} />

            <aside className="add-student" role="dialog" aria-label={t('editStudent.title')}>
                {header}

                {/* API Error Banner */}
                {apiError && (
                    <div className="add-student__error-banner" role="alert">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="12" cy="16" r="1" fill="currentColor" />
                        </svg>
                        <span>{apiError}</span>
                    </div>
                )}

                {/* Form */}
                <form className="add-student__body" onSubmit={handleSubmit} noValidate>

                    {/* — Personal Information — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                            {t('addStudent.personalInfo')}
                        </h3>

                        {/* Profile Photo Upload */}
                        <div className="add-student__photo-area">
                            <button
                                type="button"
                                className="add-student__photo-picker"
                                onClick={() => photoInputRef.current?.click()}
                                title={t('addStudent.addPhoto')}
                            >
                                {profilePreview ? (
                                    <img src={profilePreview} alt="Preview" className="add-student__photo-preview" />
                                ) : (
                                    <>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                        <span className="add-student__photo-text">{t('addStudent.addPhoto')}</span>
                                    </>
                                )}
                            </button>
                            <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/*"
                                className="add-student__file-hidden"
                                onChange={handlePhotoChange}
                            />
                            {profilePreview ? (
                                <div className="add-student__photo-info">
                                    <span className="add-student__photo-name">{profilePhoto?.name ?? t('addStudent.addPhoto')}</span>
                                    <button type="button" className="add-student__remove-file" onClick={removePhoto} title={t('addStudent.removePhoto')}>×</button>
                                </div>
                            ) : (
                                <span className="add-student__photo-hint">{t('addStudent.photoHint')}</span>
                            )}
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">
                                    {t('addStudent.firstName')} <span className="add-student__required">*</span>
                                </label>
                                <input
                                    className={fieldClass('first_name')}
                                    type="text"
                                    placeholder="e.g. Aarav"
                                    value={form.first_name}
                                    onChange={(e) => handleChange('first_name', e.target.value)}
                                    autoFocus
                                />
                                {errors.first_name && <span className="add-student__error">{errors.first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.last_name}
                                    onChange={(e) => handleChange('last_name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.email')}</label>
                                <input
                                    className={fieldClass('email')}
                                    type="email"
                                    placeholder="student@mail.com"
                                    value={form.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                />
                                {errors.email && <span className="add-student__error">{errors.email}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.phone')}</label>
                                <input
                                    className={fieldClass('phone')}
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={form.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                                {errors.phone && <span className="add-student__error">{errors.phone}</span>}
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.dateOfBirth')}</label>
                                <input
                                    className={fieldClass('date_of_birth')}
                                    type="date"
                                    value={form.date_of_birth}
                                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                                />
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.gender')}</label>
                                <select
                                    className={fieldClass('gender')}
                                    value={form.gender}
                                    onChange={(e) => handleChange('gender', e.target.value)}
                                >
                                    <option value="">{t('common.selectGender')}</option>
                                    <option value="M">{t('common.male')}</option>
                                    <option value="F">{t('common.female')}</option>
                                    <option value="Other">{t('common.other')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.aadhaarNumber')}</label>
                                <input
                                    className={fieldClass('aadhaar_number')}
                                    type="text"
                                    placeholder="e.g. 1234 5678 9012"
                                    value={form.aadhaar_number}
                                    onChange={(e) => handleChange('aadhaar_number', e.target.value)}
                                />
                                {errors.aadhaar_number && <span className="add-student__error">{errors.aadhaar_number}</span>}
                            </div>
                        </div>
                    </section>

                    {/* — Address — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                            </svg>
                            {t('addStudent.address')}
                        </h3>

                        <div className="add-student__field">
                            <label className="add-student__label">{t('addStudent.streetAddress')}</label>
                            <input
                                className={fieldClass('address')}
                                type="text"
                                placeholder={t('addStudent.streetPlaceholder')}
                                value={form.address}
                                onChange={(e) => handleChange('address', e.target.value)}
                            />
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.state')}</label>
                                <select
                                    className={fieldClass('state')}
                                    value={form.state}
                                    onChange={(e) => {
                                        const selected = availableStates.find((s) => s.name === e.target.value);
                                        handleChange('state', e.target.value);
                                        handleChange('district', '');
                                        handleChange('city', '');
                                        setSelectedStateId(selected?.id ?? null);
                                    }}
                                >
                                    <option value="">Select State…</option>
                                    {availableStates.map((s) => (
                                        <option key={s.id} value={s.name}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">District</label>
                                <select
                                    className={fieldClass('district')}
                                    value={form.district}
                                    disabled={availableDistricts.length === 0}
                                    onChange={(e) => handleChange('district', e.target.value)}
                                >
                                    <option value="">{selectedStateId === null ? 'Select State first…' : 'Select District…'}</option>
                                    {availableDistricts.map((d) => (
                                        <option key={d.id} value={d.name}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.city')}</label>
                                <select
                                    className={fieldClass('city')}
                                    value={form.city}
                                    disabled={availableCities.length === 0}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                >
                                    <option value="">{selectedStateId === null ? 'Select State first…' : 'Select City…'}</option>
                                    {availableCities.map((c) => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.postalCode')}</label>
                                <input
                                    className={fieldClass('postal_code')}
                                    type="text"
                                    placeholder="e.g. 226001"
                                    value={form.postal_code}
                                    onChange={(e) => handleChange('postal_code', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.country')}</label>
                                <input
                                    className={fieldClass('country')}
                                    type="text"
                                    placeholder="e.g. India"
                                    value={form.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    {/* — Academic Information — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
                            </svg>
                            {t('addStudent.academicInfo')}
                        </h3>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('editStudent.enrollmentNumber')}</label>
                                <input
                                    className={fieldClass('enrollment_number')}
                                    type="text"
                                    placeholder="e.g. STU-2026-001"
                                    value={form.enrollment_number}
                                    onChange={(e) => handleChange('enrollment_number', e.target.value)}
                                />
                                {errors.enrollment_number && <span className="add-student__error">{errors.enrollment_number}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('editStudent.rollNumber')}</label>
                                <input
                                    className={fieldClass('roll_number')}
                                    type="text"
                                    placeholder="e.g. 24"
                                    value={form.roll_number}
                                    onChange={(e) => handleChange('roll_number', e.target.value)}
                                />
                                {errors.roll_number && <span className="add-student__error">{errors.roll_number}</span>}
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('editStudent.admissionDate')}</label>
                                <input
                                    className={fieldClass('admission_date')}
                                    type="date"
                                    value={form.admission_date}
                                    onChange={(e) => handleChange('admission_date', e.target.value)}
                                />
                                {errors.admission_date && <span className="add-student__error">{errors.admission_date}</span>}
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.class')}</label>
                                <select
                                    className={fieldClass('class_name')}
                                    value={selectedClassId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedClassId(id);
                                        const cls = availableClasses.find((c) => c.id === id);
                                        handleChange('class_name', cls?.name || '');
                                        setSelectedSectionId('');
                                        handleChange('section_name', '');
                                    }}
                                >
                                    <option value="">{t('addStudent.selectClass')}</option>
                                    {availableClasses.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.section')}</label>
                                <select
                                    className={fieldClass('section_name')}
                                    value={selectedSectionId}
                                    disabled={!selectedClassId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedSectionId(id);
                                        const sec = availableSections.find((s) => s.id === id);
                                        handleChange('section_name', sec?.name || '');
                                    }}
                                >
                                    <option value="">{t('addStudent.selectSection')}</option>
                                    {availableSections.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.stream')}</label>
                                <select
                                    className="add-student__input"
                                    value={selectedStreamId}
                                    onChange={(e) => setSelectedStreamId(e.target.value)}
                                >
                                    <option value="">{t('addStudent.selectStream')}</option>
                                    {availableStreams.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.status')}</label>
                                <select
                                    className={fieldClass('enrollment_status')}
                                    value={form.enrollment_status}
                                    onChange={(e) => handleChange('enrollment_status', e.target.value)}
                                >
                                    <option value="active">{t('common.active')}</option>
                                    <option value="inactive">{t('common.inactive')}</option>
                                    <option value="graduated">{t('common.graduated')}</option>
                                    <option value="transferred">{t('common.transferred')}</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    {/* — Transport — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="13" rx="2" /><path d="M3 10h18" />
                                <circle cx="7.5" cy="19" r="1.5" /><circle cx="16.5" cy="19" r="1.5" />
                            </svg>
                            {t('addStudent.transport')}
                        </h3>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.route')}</label>
                                <select
                                    className="add-student__input"
                                    value={selectedRouteId}
                                    onChange={(e) => setSelectedRouteId(e.target.value)}
                                >
                                    <option value="">{t('addStudent.selectRoute')}</option>
                                    {availableRoutes.map((r) => (
                                        <option key={r.id} value={r.id}>{r.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.routeMonthlyFee')}</label>
                                <output className="add-student__readout">
                                    {selectedRoute ? routeMonthlyFeeLabel(selectedRoute) : '—'}
                                </output>
                            </div>
                        </div>
                    </section>

                    {/* — Parents — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            {t('addStudent.parents')}
                        </h3>

                        {/* Father */}
                        <h4 className="add-student__subsection-title">{t('addStudent.father')}</h4>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">
                                    {t('addStudent.firstName')} <span className="add-student__required">*</span>
                                </label>
                                <input
                                    className={fieldClass('father_first_name')}
                                    type="text"
                                    placeholder="e.g. Rajesh"
                                    value={form.father_first_name}
                                    onChange={(e) => handleChange('father_first_name', e.target.value)}
                                />
                                {errors.father_first_name && <span className="add-student__error">{errors.father_first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('father_last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.father_last_name}
                                    onChange={(e) => handleChange('father_last_name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.phone')}</label>
                                <input
                                    className={fieldClass('father_phone')}
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={form.father_phone}
                                    onChange={(e) => handleChange('father_phone', e.target.value)}
                                />
                                {errors.father_phone && <span className="add-student__error">{errors.father_phone}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.email')}</label>
                                <input
                                    className={fieldClass('father_email')}
                                    type="email"
                                    placeholder="parent@mail.com"
                                    value={form.father_email}
                                    onChange={(e) => handleChange('father_email', e.target.value)}
                                />
                                {errors.father_email && <span className="add-student__error">{errors.father_email}</span>}
                            </div>
                        </div>

                        {/* Mother */}
                        <h4 className="add-student__subsection-title">{t('addStudent.mother')}</h4>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.firstName')}</label>
                                <input
                                    className={fieldClass('mother_first_name')}
                                    type="text"
                                    placeholder="e.g. Sunita"
                                    value={form.mother_first_name}
                                    onChange={(e) => handleChange('mother_first_name', e.target.value)}
                                />
                                {errors.mother_first_name && <span className="add-student__error">{errors.mother_first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('mother_last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.mother_last_name}
                                    onChange={(e) => handleChange('mother_last_name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.phone')}</label>
                                <input
                                    className={fieldClass('mother_phone')}
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={form.mother_phone}
                                    onChange={(e) => handleChange('mother_phone', e.target.value)}
                                />
                                {errors.mother_phone && <span className="add-student__error">{errors.mother_phone}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.email')}</label>
                                <input
                                    className={fieldClass('mother_email')}
                                    type="email"
                                    placeholder="parent@mail.com"
                                    value={form.mother_email}
                                    onChange={(e) => handleChange('mother_email', e.target.value)}
                                />
                                {errors.mother_email && <span className="add-student__error">{errors.mother_email}</span>}
                            </div>
                        </div>
                    </section>

                    {/* — Footer Actions — */}
                    <footer className="add-student__footer">
                        <button type="button" className="add-student__btn add-student__btn--cancel" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="add-student__btn add-student__btn--save" disabled={saving}>
                            {saving ? (
                                <span className="add-student__saving">
                                    <span className="add-student__saving-dot" />
                                    <span className="add-student__saving-dot" />
                                    <span className="add-student__saving-dot" />
                                </span>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    {t('editStudent.saveChanges')}
                                </>
                            )}
                        </button>
                    </footer>
                </form>
            </aside>
        </>
    );
}
